/**
 * MenuItem.tsx - Individual Menu Item Component
 * 
 * Universal menu item component with full OpCentral orchestrator integration
 * Migrated from Interfacr SideMenuItem with enhancements
 * 
 * FEATURES:
 * - All menu item types (action, link, submenu, separator, custom)
 * - Drag and drop reordering (Draggr)
 * - Action logging (Actr)
 * - Undo/redo support (Backtrackr)
 * - Icon, badge, shortcut support
 * - Disabled/hidden states
 * - Custom rendering
 * - Submenu expansion
 * - Keyboard navigation
 * 
 * ARCHITECTURE:
 * - Uses @opcentral/draggr for drag and drop
 * - Uses @opcentral/actr for action logging
 * - Uses @opcentral/backtrackr for undo/redo
 * 
 * CHANGE HISTORY:
 * - 251115_1700 [current] danielbeer: Created with orchestrator integration
 */

import React, { useCallback, useMemo } from 'react';
import { useDraggable, useDroppable } from '@opcentral/draggr';
import { useBacktrackr } from '@opcentral/backtrackr';
import type { MenuItemProps } from './types';

export const MenuItem: React.FC<MenuItemProps> = ({
  item,
  collapsed = false,
  selected = false,
  expanded = false,
  level = 0,
  draggable = false,
  index,
  onClick,
  onToggleExpanded,
  onReorder,
  className = ''
}) => {
  const backtrackr = useBacktrackr() as any;
  
  // Separator - simple divider
  if (item.type === 'separator') {
    return <div className="menu-item__separator" />;
  }
  
  // Custom render - delegate to custom renderer
  if (item.render) {
    return <div className="menu-item__custom">{item.render(item)}</div>;
  }
  
  // Draggr integration for drag and drop
  const dragProps = useDraggable(item.id, {
    type: 'menu-item',
    data: { item, index },
    disabled: !draggable || item.disabled || item.hidden,
    onDragStart: () => {
      actr?.execute('menu:item-drag-start', {
        itemId: item.id,
        label: item.label,
        index
      });
    },
    onDragEnd: () => {
      actr?.execute('menu:item-drag-end', {
        itemId: item.id
      });
    }
  });
  
  const dropProps = useDroppable(`menu-item-${item.id}`, {
    accepts: ['menu-item'],
    onDrop: (source: any) => {
      if (source.data.index !== index && onReorder) {
        const oldIndex = source.data.index;
        const newIndex = index!;
        
        // Record for undo/redo
        if (backtrackr) {
          backtrackr.recordChange({
            type: 'menu:item-reorder',
            description: `Reorder menu item from ${oldIndex} to ${newIndex}`,
            context: null,
            before: { index: oldIndex },
            after: { index: newIndex },
            metadata: { itemId: source.data.item.id }
          });
        }
        
        // Log action
        actr?.execute('menu:item-reorder', {
          from: oldIndex,
          to: newIndex,
          itemId: source.data.item.id,
          label: source.data.item.label
        });
        
        // Execute reorder
        onReorder(oldIndex, newIndex);
      }
    }
  });
  
  const isDragging = dragProps.isDragging;
  const isOver = dropProps.isOver;
  
  // Combine refs for drag and drop
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      if (draggable) {
        dragProps.setNodeRef(node);
        dropProps.setNodeRef(node);
      }
    },
    [draggable, dragProps, dropProps]
  );
  
  // Item classes
  const itemClasses = useMemo(() => [
    'menu-item',
    selected ? 'menu-item--selected' : '',
    item.disabled ? 'menu-item--disabled' : '',
    item.type === 'submenu' ? 'menu-item--submenu' : '',
    expanded ? 'menu-item--expanded' : '',
    isDragging ? 'menu-item--dragging' : '',
    isOver ? 'menu-item--drag-over' : '',
    draggable && !item.disabled ? 'menu-item--draggable' : '',
    item.className || '',
    className
  ].filter(Boolean).join(' '), [
    selected,
    item.disabled,
    item.type,
    item.className,
    expanded,
    isDragging,
    isOver,
    draggable,
    className
  ]);
  
  // Handle click
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent bubbling to parent items
    if (item.disabled) return;
    
    // Log action
    actr?.execute('menu:item-click', {
      itemId: item.id,
      label: item.label,
      type: item.type
    });
    
    // 1. Execute item's action if it has one
    if (item.action) {
      console.log('[MenuItem] Executing action for:', item.label, item.id);
      item.action();
    } else {
      console.log('[MenuItem] No action defined for:', item.label, item.id);
    }
    
    // 2. Handle special types
    if (item.type === 'link' && item.href) {
      window.open(item.href, item.target || '_self');
    }
    
    if (item.type === 'submenu') {
      // Toggle expansion and notify parent
      if (onToggleExpanded) {
        onToggleExpanded();
      }
      onClick(); // Only call onClick for submenu items (for selection state)
    }
    
    // For action items, don't call onClick - it would toggle parent submenu
  }, [item, actr, onClick, onToggleExpanded]);
  
  // Handle submenu toggle
  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    actr?.execute('menu:item-toggle', {
      itemId: item.id,
      label: item.label,
      expanded: !expanded
    });
    
    if (onToggleExpanded) {
      onToggleExpanded();
    }
  }, [item, expanded, actr, onToggleExpanded]);
  
  // Indentation for nested items
  const indentStyle = useMemo(() => ({
    paddingLeft: `${level * 1.5}rem`,
    ...item.customStyles
  }), [level, item.customStyles]);
  
  return (
    <div
      ref={setRefs}
      className="menu-item__wrapper"
      style={indentStyle}
      {...(draggable ? dragProps.attributes : {})}
      {...(draggable ? dragProps.listeners : {})}
    >
      <button
        className={itemClasses}
        onClick={handleClick}
        disabled={item.disabled}
        title={collapsed ? item.label : item.tooltip}
        aria-label={item.label}
        aria-expanded={item.type === 'submenu' ? expanded : undefined}
        aria-selected={selected}
        aria-disabled={item.disabled}
      >
        {/* Icon */}
        {item.icon && (
          <span className="menu-item__icon" aria-hidden="true">
            {item.icon}
          </span>
        )}
        
        {/* Label (hidden when collapsed) */}
        {!collapsed && (
          <span className="menu-item__label">{item.label}</span>
        )}
        
        {/* Badge */}
        {!collapsed && item.badge && (
          <span className="menu-item__badge" aria-label={`${item.badge} notifications`}>
            {item.badge}
          </span>
        )}
        
        {/* Submenu indicator */}
        {!collapsed && item.type === 'submenu' && (
          <span
            className="menu-item__toggle"
            onClick={handleToggle}
            role="button"
            aria-label={expanded ? 'Collapse submenu' : 'Expand submenu'}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                if (onToggleExpanded) {
                  onToggleExpanded();
                }
              }
            }}
          >
            {expanded ? '▾' : '▸'}
          </span>
        )}
        
        {/* Shortcut */}
        {!collapsed && item.shortcut && (
          <span className="menu-item__shortcut" aria-label={`Keyboard shortcut: ${item.shortcut}`}>
            {item.shortcut}
          </span>
        )}
        
        {/* Drag handle indicator */}
        {draggable && !item.disabled && !collapsed && (
          <span className="menu-item__drag-handle" aria-hidden="true">
            ⋮⋮
          </span>
        )}
      </button>
      
      {/* Children (submenu items) */}
      {item.type === 'submenu' && expanded && item.children && !collapsed && (
        <div className="menu-item__children" role="group" aria-label={`${item.label} submenu`}>
          {item.children.map((child, childIndex) => (
            <MenuItem
              key={child.id}
              item={child}
              collapsed={collapsed}
              selected={selected && child.id === item.id}
              expanded={false}
              level={level + 1}
              draggable={draggable}
              index={childIndex}
              onClick={onClick}
              onToggleExpanded={onToggleExpanded}
              onReorder={onReorder}
            />
          ))}
        </div>
      )}
    </div>
  );
};

MenuItem.displayName = 'MenuItem';
